import EnumDataSource from "utils/enum-data-source.util";

export const TEMPLATES_LIMIT = 25;

export const TEMPLATE_TYPE_CHART_NOTE = 1;
export const TEMPLATE_TYPE_REPORT_SUMMARY = 2;
export const TEMPLATE_TYPE_DOCUMENT = 3;
export const TEMPLATE_TYPE_MESSAGE = 4;
export const TEMPLATE_TYPE_FAX = 5;
export const TEMPLATE_TYPE_CLASSIC_EMAIL = 6;
export const TEMPLATE_TYPE_TASK = 7;
export const TEMPLATE_TYPE_SALE_NOTE = 8;
export const TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE = 9;
export const TEMPLATE_TYPE_FORM = 10;
export const TEMPLATE_TYPE_PURCHASE_AGREEMENT = 11;
export const TEMPLATE_TYPE_DESIGNER_EMAIL = 12;
// ENG-2277 (HTML Forms): a form printed on its own page and offered on the surfaces it is placed on.
// Not a Form (10), which keeps its master behaviour; Digital Forms have their own Settings section.
export const TEMPLATE_TYPE_DIGITAL_FORM = 13;
export const TEMPLATE_TYPE_SNIPPET = 99;

export const TEMPLATE_TYPES_NON_FORMS = [
  TEMPLATE_TYPE_CHART_NOTE,
  TEMPLATE_TYPE_REPORT_SUMMARY,
  TEMPLATE_TYPE_DOCUMENT,
  TEMPLATE_TYPE_MESSAGE,
  TEMPLATE_TYPE_FAX,
  TEMPLATE_TYPE_CLASSIC_EMAIL,
  TEMPLATE_TYPE_DESIGNER_EMAIL,
  TEMPLATE_TYPE_TASK,
  TEMPLATE_TYPE_SALE_NOTE,
  TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE,
  TEMPLATE_TYPE_SNIPPET,
  TEMPLATE_TYPE_FORM
];

export const TEMPLATE_TYPES_FORMS = [TEMPLATE_TYPE_PURCHASE_AGREEMENT];

export const TEMPLATE_TYPES_DIGITAL_FORMS = [TEMPLATE_TYPE_DIGITAL_FORM];

const templateTypes = {
  [TEMPLATE_TYPE_CHART_NOTE]: "Chart Note",
  [TEMPLATE_TYPE_REPORT_SUMMARY]: "Report Summary",
  [TEMPLATE_TYPE_DOCUMENT]: "Document",
  [TEMPLATE_TYPE_MESSAGE]: "Text Message",
  [TEMPLATE_TYPE_FAX]: "Fax Cover Letter",
  [TEMPLATE_TYPE_CLASSIC_EMAIL]: "Classic Email",
  [TEMPLATE_TYPE_DESIGNER_EMAIL]: "Designer Email",
  [TEMPLATE_TYPE_SALE_NOTE]: "Sale Notes/Memo",
  [TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE]: "New Appointment Note",
  [TEMPLATE_TYPE_SNIPPET]: "Snippet"
};

const textTemplateTypesOrder = {
  [TEMPLATE_TYPE_CHART_NOTE]: 1,
  [TEMPLATE_TYPE_REPORT_SUMMARY]: 2,
  [TEMPLATE_TYPE_DOCUMENT]: 3,
  [TEMPLATE_TYPE_MESSAGE]: 4,
  [TEMPLATE_TYPE_FAX]: 5,
  [TEMPLATE_TYPE_CLASSIC_EMAIL]: 6,
  [TEMPLATE_TYPE_DESIGNER_EMAIL]: 7,
  [TEMPLATE_TYPE_SALE_NOTE]: 8,
  [TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE]: 9,
  [TEMPLATE_TYPE_SNIPPET]: 10,
  [TEMPLATE_TYPE_TASK]: 11,
  [TEMPLATE_TYPE_FORM]: 12,
  [TEMPLATE_TYPE_PURCHASE_AGREEMENT]: 13,
  [TEMPLATE_TYPE_DIGITAL_FORM]: 14
};

export const DEFAULT_TEMPLATE_TYPES = [
  TEMPLATE_TYPE_CHART_NOTE,
  TEMPLATE_TYPE_REPORT_SUMMARY,
  TEMPLATE_TYPE_SNIPPET
];

export const EMAIL_TEMPLATE_TYPES = [
  TEMPLATE_TYPE_CLASSIC_EMAIL,
  TEMPLATE_TYPE_SNIPPET
];

export const EMAIL_TEMPLATE_WITH_DESIGN_TYPES = [
  TEMPLATE_TYPE_CLASSIC_EMAIL,
  TEMPLATE_TYPE_SNIPPET,
  TEMPLATE_TYPE_DESIGNER_EMAIL
];

// For the Drag & Drop builder's picker only. A classic editor beside it takes
// EMAIL_TEMPLATE_WITH_DESIGN_TYPES; given this list it hides Classic templates (SN-19838).
export const EMAIL_TEMPLATE_DESIGN_ONLY_TYPES = [TEMPLATE_TYPE_DESIGNER_EMAIL];

export const FAX_TEMPLATE_TYPES = [TEMPLATE_TYPE_FAX, TEMPLATE_TYPE_SNIPPET];

export const DOCUMENT_TEMPLATE_TYPES = [
  ...EMAIL_TEMPLATE_TYPES,
  TEMPLATE_TYPE_DOCUMENT
];

export const TASK_TEMPLATE_TYPES = [
  TEMPLATE_TYPE_TASK,
  TEMPLATE_TYPE_MESSAGE,
  TEMPLATE_TYPE_SNIPPET
];

export const MESSAGE_TEMPLATE_TYPES = [TEMPLATE_TYPE_MESSAGE];

export const LEADS_SETTINGS_TEMPLATE_TYPE_CODES = [
  TEMPLATE_TYPE_MESSAGE,
  TEMPLATE_TYPE_CLASSIC_EMAIL,
  TEMPLATE_TYPE_DESIGNER_EMAIL,
  TEMPLATE_TYPE_SNIPPET
];

export const LEADS_INBOX_SMS_TEMPLATE_TYPES = [
  ...MESSAGE_TEMPLATE_TYPES,
  TEMPLATE_TYPE_SNIPPET
];

export const PRINTABLE_FORM_TEMPLATE_TYPES = [
  TEMPLATE_TYPE_FORM,
  TEMPLATE_TYPE_CHART_NOTE,
  TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE,
  TEMPLATE_TYPE_SNIPPET
];

export const TEXT_TEMPLATE_TYPES = new EnumDataSource(
  templateTypes,
  textTemplateTypesOrder
);

export const TEXT_TEMPLATE_TYPES_V2 = new EnumDataSource(
  {
    ...templateTypes,
    [TEMPLATE_TYPE_TASK]: "Task"
  },
  textTemplateTypesOrder
);

export const TEXT_TEMPLATE_TYPES_V3 = new EnumDataSource(
  {
    ...templateTypes,
    [TEMPLATE_TYPE_TASK]: "Task",
    [TEMPLATE_TYPE_FORM]: "Form",
    [TEMPLATE_TYPE_PURCHASE_AGREEMENT]: "Purchase Agreement",
    [TEMPLATE_TYPE_DIGITAL_FORM]: "Digital Form"
  },
  textTemplateTypesOrder
);

export const TEXT_TEMPLATE_TYPES_NON_FORMS = new EnumDataSource(
  {
    ...templateTypes,
    [TEMPLATE_TYPE_TASK]: "Task",
    [TEMPLATE_TYPE_FORM]: "Form"
  },
  textTemplateTypesOrder
);

export const TEXT_TEMPLATE_TYPES_FORMS = new EnumDataSource({
  [TEMPLATE_TYPE_PURCHASE_AGREEMENT]: "Purchase Agreement"
});

export const TEXT_TEMPLATE_TYPES_DIGITAL_FORMS = new EnumDataSource({
  [TEMPLATE_TYPE_DIGITAL_FORM]: "Digital Form"
});

export const INVOICE_TEMPLATE_TYPES = [
  TEMPLATE_TYPE_SALE_NOTE,
  TEMPLATE_TYPE_SNIPPET
];

export const TEXT_ONLY_TEMPLATE_TYPES = [
  TEMPLATE_TYPE_MESSAGE,
  TEMPLATE_TYPE_TASK,
  TEMPLATE_TYPE_SALE_NOTE,
  TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE
];

export const TEXT_MESSAGE_MAX_LENGTH = 320;
/**
 * Cap for the note-shaped text-only template bodies — Task, Sale Note, New
 * Appointment Note. `TextTemplate.text` is an unbounded `TextField`, so this is
 * a UI guard against a body no consuming field can hold, not a backend limit.
 *
 * Explicitly NOT an SMS limit. SMS surfaces measure segments instead
 * (`utils/sms-segments.util.ts`): carriers reject anything over 10 concatenated
 * segments — 1,530 GSM-7 characters, or 670 once one emoji flips the message to
 * UCS-2 — so a character cap of any size is the wrong instrument there.
 */
export const TEXT_TEMPLATE_BODY_MAX_LENGTH = 1600;
export const EMAIL_SUBJECT_MAX_LENGTH = 255;
